
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CalendarConnection, CalendarSelection } from "@/types/calendar";
import { Settings, Trash2, ChevronDown, ChevronUp, Calendar, CheckCircle, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Define calendar item type to match the API response
type CalendarItem = {
  id: string;
  summary?: string;
  name?: string;
  displayName?: string;
  title?: string;
  description?: string;
  primary?: boolean;
  accessRole?: string;
  summaryOverride?: string;
};

interface CalendarConnectionCardProps {
  calendarConnection: CalendarConnection;
  onUpdated: (connection: CalendarConnection) => void;
  onDisconnect: (connection: CalendarConnection) => void;
  autoExpandSettings?: boolean;
  shouldRedirectToHome?: boolean;
  allConnections: CalendarConnection[];
}

const CalendarConnectionCard = ({
  calendarConnection,
  onUpdated,
  onDisconnect,
  autoExpandSettings = false,
  shouldRedirectToHome = false,
  allConnections,
}: CalendarConnectionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(autoExpandSettings);
  const [selectedCalendars, setSelectedCalendars] = useState<CalendarSelection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [calendars, setCalendars] = useState<CalendarItem[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentUser, checkSlackConnectionsAndRedirect } = useAuth();

  // Extract account info from token
  const getAccountInfoFromToken = (connection: CalendarConnection): { name?: string; email?: string } => {
    try {
      if (connection.provider === 'google') {
        // For Google, try to get email from the primary calendar ID
        const primaryCalendar = connection.selected_calendars?.find(cal => cal.isPrimary);
        if (primaryCalendar && primaryCalendar.id.includes('@')) {
          return { email: primaryCalendar.id };
        }
      } else if (connection.provider === 'outlook') {
        // For Outlook, decode the JWT token to get user info
        const token = connection.access_token;
        if (token) {
          const payload = token.split('.')[1];
          if (payload) {
            try {
              const decoded = JSON.parse(atob(payload));
              return {
                name: decoded.name,
                email: decoded.unique_name || decoded.upn
              };
            } catch (e) {
              console.log("Could not decode Outlook token");
            }
          }
        }
      }
    } catch (error) {
      console.log("Error extracting account info from token:", error);
    }
    return {};
  };

  // Get calendar display name - same logic as CalendarList
  const getCalendarDisplayName = (calendar: CalendarItem): string => {
    console.log("CalendarConnectionCard - Getting calendar name for:", calendar.id);
    
    // For Google Calendar: prioritize summaryOverride, then summary
    if (calendar.summaryOverride && calendar.summaryOverride.trim() !== '') {
      console.log("Using summaryOverride:", calendar.summaryOverride);
      return calendar.summaryOverride.trim();
    }
    
    if (calendar.summary && calendar.summary.trim() !== '') {
      console.log("Using summary:", calendar.summary);
      return calendar.summary.trim();
    }
    
    // For Outlook: use name field
    if (calendar.name && calendar.name.trim() !== '') {
      console.log("Using name:", calendar.name);
      return calendar.name.trim();
    }
    
    // Other possible fields
    if (calendar.displayName && calendar.displayName.trim() !== '') {
      console.log("Using displayName:", calendar.displayName);
      return calendar.displayName.trim();
    }
    
    if (calendar.title && calendar.title.trim() !== '') {
      console.log("Using title:", calendar.title);
      return calendar.title.trim();
    }
    
    console.log("CalendarConnectionCard - NO VALID NAME FOUND for:", calendar.id);
    return 'Unnamed Calendar';
  };

  useEffect(() => {
    if (autoExpandSettings) {
      setIsExpanded(true);
      // Automatically fetch calendars when auto-expanding
      fetchCalendars();
    }
  }, [autoExpandSettings]);

  useEffect(() => {
    if (calendarConnection.selected_calendars) {
      setSelectedCalendars(calendarConnection.selected_calendars);
    }
  }, [calendarConnection.selected_calendars]);

  const fetchCalendars = async () => {
    if (calendars.length > 0) return;
    
    setIsLoading(true);
    try {
      const endpoint = calendarConnection.provider === 'google' 
        ? 'get-google-calendars'
        : 'get-outlook-calendars';
      
      const response = await fetch(
        `https://erticlijceutpetbahff.supabase.co/functions/v1/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({
            connectionId: calendarConnection.id
          }),
        }
      );
      
      const data = await response.json();
      if (data.calendars) {
        console.log("CalendarConnectionCard - Received calendars:", data.calendars.length);
        setCalendars(data.calendars);
      }
    } catch (error) {
      console.error("Error fetching calendars:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch calendars. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      fetchCalendars();
    }
  };

  const handleCalendarToggle = (calendarId: string) => {
    setSelectedCalendars(prev => {
      const isSelected = prev.some(cal => cal.id === calendarId);
      if (isSelected) {
        return prev.filter(cal => cal.id !== calendarId);
      } else {
        const calendar = calendars.find(cal => cal.id === calendarId);
        const calendarName = calendar ? getCalendarDisplayName(calendar) : calendarId;
        // If this is the first calendar being selected, make it primary
        const isFirstCalendar = prev.length === 0;
        return [...prev, { 
          id: calendarId, 
          name: calendarName,
          isPrimary: isFirstCalendar 
        }];
      }
    });
  };

  const handleSetCalendarPrimary = (calendarId: string) => {
    setSelectedCalendars(prev => 
      prev.map(cal => ({
        ...cal,
        isPrimary: cal.id === calendarId
      }))
    );
  };

  const handleSaveCalendars = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('calendar_connections')
        .update({ selected_calendars: selectedCalendars as unknown as string[] })
        .eq('id', calendarConnection.id);
      
      if (error) throw error;
      
      const updatedConnection = {
        ...calendarConnection,
        selected_calendars: selectedCalendars
      };
      
      onUpdated(updatedConnection);
      
      toast({
        title: "Calendars Updated",
        description: `Selected ${selectedCalendars.length} calendar(s) for syncing.`,
      });

      // Collapse the calendar selection section after successful save
      setIsExpanded(false);

      if (shouldRedirectToHome) {
        // After calendar setup, check if user needs Slack setup
        toast({
          title: "Calendars connected! 📅",
          description: "Great! Now let's connect your Slack to stay on top of conversations.",
        });
        
        setTimeout(async () => {
          await checkSlackConnectionsAndRedirect(currentUser);
        }, 2000);
      }
    } catch (error) {
      console.error("Error updating calendars:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update calendar selection. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = (connection: CalendarConnection) => {
    // Close dropdown immediately and manage focus properly
    setDropdownOpen(false);
    
    // Use setTimeout to ensure dropdown closes before calling onDisconnect
    setTimeout(() => {
      // Blur any active element to prevent focus issues
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.blur) {
        activeElement.blur();
      }
      
      // Call the disconnect handler
      onDisconnect(connection);
    }, 50);
  };

  const handleSetPrimary = async () => {
    // Close dropdown first
    setDropdownOpen(false);
    
    try {
      // First, update all OTHER connections to not be primary
      const updatePromises = allConnections
        .filter(conn => conn.id !== calendarConnection.id)
        .map(async (conn) => {
          // Remove primary status from all calendars in other connections
          const updatedCalendars = conn.selected_calendars?.map(cal => ({
            ...cal,
            isPrimary: false
          })) || [];
          
          return supabase
            .from('calendar_connections')
            .update({ 
              selected_calendars: updatedCalendars as unknown as string[]
            })
            .eq('id', conn.id);
        });
      
      // Wait for all other connections to be updated
      const results = await Promise.all(updatePromises);
      for (const result of results) {
        if (result.error) throw result.error;
      }
      
      // Now set this connection's first selected calendar as primary
      const updatedCalendars = selectedCalendars.map((cal, index) => ({
        ...cal,
        isPrimary: index === 0 // Make first calendar primary
      }));
      
      const { error } = await supabase
        .from('calendar_connections')
        .update({ selected_calendars: updatedCalendars as unknown as string[] })
        .eq('id', calendarConnection.id);
      
      if (error) throw error;
      
      const updatedConnection = {
        ...calendarConnection,
        selected_calendars: updatedCalendars
      };
      
      onUpdated(updatedConnection);
      
      toast({
        title: "Primary Calendar Set",
        description: `${getConnectionDisplayName()} is now your primary calendar.`,
      });
    } catch (error) {
      console.error("Error setting primary calendar:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set primary calendar. Please try again.",
      });
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Google Calendar';
      case 'outlook':
        return 'Outlook Calendar';
      default:
        return 'Calendar';
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return '🗓️';
      case 'outlook':
        return '📅';
      default:
        return '📅';
    }
  };

  const getConnectionDisplayName = () => {
    // For Google and Outlook accounts, prioritize showing email to distinguish between different accounts
    if (calendarConnection.provider === 'google' || calendarConnection.provider === 'outlook') {
      if (calendarConnection.account_email && calendarConnection.account_email.trim() !== '') {
        return calendarConnection.account_email;
      }
    }
    
    // For other providers or if no email, check if we have stored account info
    if (calendarConnection.account_name && calendarConnection.account_name.trim() !== '') {
      return calendarConnection.account_name;
    }
    if (calendarConnection.account_email && calendarConnection.account_email.trim() !== '') {
      return calendarConnection.account_email;
    }
    
    // If not, try to extract from token
    const accountInfo = getAccountInfoFromToken(calendarConnection);
    if ((calendarConnection.provider === 'google' || calendarConnection.provider === 'outlook') && accountInfo.email && accountInfo.email.trim() !== '') {
      return accountInfo.email;
    }
    if (accountInfo.name && accountInfo.name.trim() !== '') {
      return accountInfo.name;
    }
    if (accountInfo.email && accountInfo.email.trim() !== '') {
      return accountInfo.email;
    }
    
    // Fall back to provider name
    return getProviderDisplayName(calendarConnection.provider);
  };

  // Check if this connection has a primary calendar selected
  const hasPrimaryCalendar = selectedCalendars.some(cal => cal.isPrimary);
  // Check if this is the only connection with a primary calendar across ALL connections
  const isPrimaryConnection = hasPrimaryCalendar && !allConnections.some(conn => 
    conn.id !== calendarConnection.id && 
    conn.selected_calendars?.some(cal => cal.isPrimary)
  );
  const canSetPrimary = !isPrimaryConnection && allConnections.length > 1;

  return (
    <Card className="w-full border-2 transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1 pr-2">
            <div className="text-2xl flex-shrink-0">{getProviderIcon(calendarConnection.provider)}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base truncate">
                  {getConnectionDisplayName()}
                </h3>
                {isPrimaryConnection && (
                  <Badge variant="default" className="bg-green-100 text-green-800 border-green-300 flex-shrink-0">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Primary
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {getProviderDisplayName(calendarConnection.provider)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 z-50">
                {canSetPrimary && (
                  <DropdownMenuItem onClick={handleSetPrimary} className="text-sm whitespace-nowrap">
                    <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span>Set as Primary</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => handleDisconnect(calendarConnection)}
                  className="text-destructive focus:text-destructive text-sm whitespace-nowrap"
                >
                  <Trash2 className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>Disconnect</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <Collapsible open={isExpanded} onOpenChange={handleExpandToggle}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <span className="text-sm font-medium">Calendar Selection</span>
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="space-y-3 mt-3">
            <Separator />
            
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="text-sm text-muted-foreground">Loading calendars...</div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm font-medium">
                  Select calendars to sync ({selectedCalendars.length} selected)
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {calendars.map((calendar) => {
                    const displayName = getCalendarDisplayName(calendar);
                    const isSelected = selectedCalendars.some(cal => cal.id === calendar.id);
                    const isPrimaryCalendar = selectedCalendars.find(cal => cal.id === calendar.id)?.isPrimary;
                    
                    console.log("Rendering calendar:", calendar.id, "with name:", displayName);
                    
                    return (
                      <div key={calendar.id} className="flex items-start space-x-3 p-2 rounded-md hover:bg-muted min-h-[2.5rem]">
                        <Switch
                          checked={isSelected}
                          onCheckedChange={() => handleCalendarToggle(calendar.id)}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-start gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium break-words leading-tight">
                                  {displayName}
                                </div>
                                {isPrimaryCalendar && (
                                  <Crown className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                                )}
                              </div>
                              {calendar.description && (
                                <div className="text-xs text-muted-foreground break-words leading-tight mt-1">
                                  {calendar.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {isSelected && !isPrimaryCalendar && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSetCalendarPrimary(calendar.id)}
                            className="text-xs px-2 py-1 h-auto flex-shrink-0"
                          >
                            <Crown className="h-3 w-3 mr-1" />
                            Set Primary
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={handleSaveCalendars} 
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading ? "Saving..." : "Save Selection"}
                  </Button>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default CalendarConnectionCard;
